import React, { useState, useEffect, useMemo } from "react"; // Import React and hooks
import axios from "axios"; // Import axios for API calls

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableContainer from "@mui/material/TableContainer";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography"; // Import MDTypography for text

// Material Dashboard 2 React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import DataTable from "examples/Tables/DataTable";

// API Configuration
import API_BASE_URL from "config/api";
import { getUserTokenUsage } from "services/dashboardService";

const formatUsd = (value) => `$${Number(value || 0).toFixed(4)}`;
const formatTokens = (value) => Number(value || 0).toLocaleString();

function StudentAnalytics() {
  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizScores, setQuizScores] = useState([]);
  const [username, setUsername] = useState("");
  const [searchUsername, setSearchUsername] = useState("");
  const [tokenUsageData, setTokenUsageData] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(false);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        let url = `${API_BASE_URL}/activity/user-summary`;
        const params = new URLSearchParams();
        if (searchUsername) {
          params.append("username", searchUsername);
          url = `${url}?${params.toString()}`;
        }
        const response = await axios.get(url);
        setAnalyticsData(response.data.data);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    const fetchQuizScores = async () => {
      try {
        let url = `${API_BASE_URL}/activity/quiz-scores`;
        const params = new URLSearchParams();
        if (searchUsername) {
          params.append("username", searchUsername);
          url = `${url}?${params.toString()}`;
        }
        const response = await axios.get(url);
        setQuizScores(response.data.data);
      } catch (err) {
        console.error("Error fetching quiz scores:", err);
      }
    };

    fetchAnalytics();
    fetchQuizScores();

    const fetchTokenUsage = async () => {
      setTokenLoading(true);
      try {
        const data = await getUserTokenUsage({
          username: searchUsername || undefined,
          days: 30,
        });
        setTokenUsageData(data);
      } catch (err) {
        console.error("Error fetching token usage:", err);
        setTokenUsageData(null);
      } finally {
        setTokenLoading(false);
      }
    };

    fetchTokenUsage();
  }, [searchUsername]);

  const handleSearch = () => {
    setSearchUsername(username);
  };

  const totalTimeSpent = analyticsData.reduce((acc, feature) => acc + feature.time_spent_hours, 0);
  const totalGenerations = analyticsData.reduce((acc, feature) => acc + feature.generations, 0);
  const mostUsedFeature = analyticsData.reduce(
    (prev, current) => (prev.generations > current.generations ? prev : current),
    { feature_name: "N/A", generations: 0 }
  );

  const usageSummary = tokenUsageData?.usage || tokenUsageData?.platform || null;
  const sessionCosts = tokenUsageData?.sessions || [];
  const selectedUser = tokenUsageData?.user || null;

  const quizScoresChartData = useMemo(
    () => ({
      labels: quizScores.map((s) => new Date(s.timestamp).toLocaleDateString()),
      datasets: {
        label: "Quiz Score",
        data: quizScores.map((s) => s.score),
      },
    }),
    [quizScores]
  );

  const tableData = useMemo(() => {
    const columns = [
      { Header: "Feature Name", accessor: "feature_name", align: "left" },
      { Header: "Generations", accessor: "generations", align: "center" },
      { Header: "Time Spent (Hours)", accessor: "time_spent_hours", align: "center" },
      { Header: "Last Score", accessor: "last_score", align: "center" },
    ];

    const rows = analyticsData.map((feature) => ({
      feature_name: (
        <MDTypography variant="button" color="white">
          {feature.feature_name}
        </MDTypography>
      ),
      generations: (
        <MDTypography variant="button" color="white">
          {feature.generations}
        </MDTypography>
      ),
      time_spent_hours: (
        <MDTypography variant="button" color="white">
          {feature.time_spent_hours.toFixed(2)}
        </MDTypography>
      ),
      last_score: (
        <MDTypography variant="button" color="white">
          {feature.feature_name === "quiz" && feature.last_score !== undefined
            ? feature.last_score
            : "N/A"}
        </MDTypography>
      ),
    }));

    return { columns, rows };
  }, [analyticsData]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={4}>
          <Card>
            <MDBox p={2}>
              <Grid container spacing={3} alignItems="center">
                <Grid item>
                  <MDInput
                    type="text"
                    label="Username or Email"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </Grid>
                <Grid item>
                  <MDButton variant="gradient" color="info" onClick={handleSearch}>
                    Search
                  </MDButton>
                </Grid>
              </Grid>
            </MDBox>
          </Card>
        </MDBox>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="dark"
                icon="access_time"
                title="Total Time Spent (Hours)"
                count={totalTimeSpent.toFixed(2)}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                icon="group"
                title="Total Generations"
                count={totalGenerations}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="success"
                icon="star"
                title="Most Used Feature"
                count={mostUsedFeature.feature_name}
                percentage={{
                  color: "success",
                  amount: "",
                  label: "Just updated",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="info"
                icon="payments"
                title={searchUsername ? "Session AI Cost (30d)" : "Avg Cost / Session (30d)"}
                count={
                  usageSummary
                    ? formatUsd(usageSummary.avg_cost_per_session || usageSummary.total_cost)
                    : tokenLoading
                    ? "..."
                    : formatUsd(0)
                }
                percentage={{
                  color: "info",
                  amount: usageSummary ? formatTokens(usageSummary.total_tokens) : "0",
                  label: "Total tokens",
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <MDBox mb={1.5}>
              <ComplexStatisticsCard
                color="warning"
                icon="receipt_long"
                title={searchUsername ? "Total AI Spend (30d)" : "Platform AI Spend (30d)"}
                count={
                  usageSummary
                    ? formatUsd(usageSummary.total_cost)
                    : tokenLoading
                    ? "..."
                    : formatUsd(0)
                }
                percentage={{
                  color: "warning",
                  amount: usageSummary ? String(usageSummary.query_count || 0) : "0",
                  label: "Tracked queries",
                }}
              />
            </MDBox>
          </Grid>
        </Grid>

        {selectedUser && (
          <MDBox mt={2}>
            <MDTypography variant="button" color="text">
              Token usage for {selectedUser.name || selectedUser.username} ({selectedUser.email})
            </MDTypography>
          </MDBox>
        )}

        {!searchUsername && tokenUsageData?.users?.length > 0 && (
          <MDBox mt={4}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Per-User AI Cost (Last 30 Days)
                </MDTypography>
              </MDBox>
              <MDBox pt={3} pb={2} px={2}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="right">Sessions</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell align="right">Est. Cost</TableCell>
                        <TableCell align="right">Avg / Session</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tokenUsageData.users.slice(0, 20).map((row) => (
                        <TableRow key={row.user_id}>
                          <TableCell>{row.name || row.username}</TableCell>
                          <TableCell>{row.email}</TableCell>
                          <TableCell align="right">{row.session_count || 0}</TableCell>
                          <TableCell align="right">{formatTokens(row.total_tokens)}</TableCell>
                          <TableCell align="right">{formatUsd(row.total_cost)}</TableCell>
                          <TableCell align="right">{formatUsd(row.avg_cost_per_session)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </MDBox>
        )}

        {searchUsername && sessionCosts.length > 0 && (
          <MDBox mt={4}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="dark"
                borderRadius="lg"
                coloredShadow="dark"
              >
                <MDTypography variant="h6" color="white">
                  Session Token & Cost Breakdown
                </MDTypography>
              </MDBox>
              <MDBox pt={3} pb={2} px={2}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Session</TableCell>
                        <TableCell align="right">Queries</TableCell>
                        <TableCell align="right">Tokens</TableCell>
                        <TableCell align="right">Est. Cost</TableCell>
                        <TableCell align="right">Avg / Query</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessionCosts.map((session) => (
                        <TableRow key={session.session_id}>
                          <TableCell>{session.session_id.slice(0, 18)}...</TableCell>
                          <TableCell align="right">{session.query_count}</TableCell>
                          <TableCell align="right">{formatTokens(session.total_tokens)}</TableCell>
                          <TableCell align="right">{formatUsd(session.total_cost)}</TableCell>
                          <TableCell align="right">
                            {formatUsd(session.avg_cost_per_query)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </MDBox>
            </Card>
          </MDBox>
        )}

        <MDBox mt={4}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <MDTypography variant="h6" color="white">
                    Feature Usage Analytics
                  </MDTypography>
                </MDBox>
                <MDBox pt={3}>
                  {loading ? (
                    <MDTypography variant="body2" color="text" textAlign="center">
                      Loading analytics data...
                    </MDTypography>
                  ) : error ? (
                    <MDTypography variant="body2" color="error" textAlign="center">
                      Error loading analytics: {error.message}
                    </MDTypography>
                  ) : (
                    <DataTable
                      table={{ columns: tableData.columns, rows: tableData.rows }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  )}
                </MDBox>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: "100%" }}>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="success"
                  borderRadius="lg"
                  coloredShadow="success"
                >
                  <MDTypography variant="h6" color="white">
                    User Progress (Quiz Scores)
                  </MDTypography>
                </MDBox>
                <MDBox pt={3}>
                  {quizScores.length > 0 ? (
                    <ReportsLineChart
                      color="success"
                      title="Quiz Scores"
                      description="User's quiz scores over time"
                      date="just updated"
                      chart={quizScoresChartData}
                      height="100%"
                      username={searchUsername}
                    />
                  ) : (
                    <MDTypography variant="body2" color="text" textAlign="center">
                      No quiz scores available.
                    </MDTypography>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default StudentAnalytics;
